import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useCallback,
} from 'react';
import { z } from 'zod';
import { useCookieState, useLatest } from 'ahooks';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useAppData } from '~/modules/root';

const VOTE_UPDATE_SCHEMA = z.object({
    type: z.literal('vote'),
    buildhub_id: z.number(),
    document_id: z.number(),
    action: z.enum(['save', 'remove']),
    reaction: z.string(),
    up_vote_count: z.number(),
    down_vote_count: z.number(),
    member_id: z.number(),
});

const COMMENT_UPDATE_SCHEMA = z.object({
    type: z.literal('comment'),
    buildhub_id: z.number(),
    document_id: z.number(),
    comment_id: z.number(),
    action: z.literal('create'),
});

const PRESENCE_USER_SCHEMA = z.object({
    id: z.number(),
    name: z.string(),
    avatar: z.string().nullable(),
    initials: z.string(),
});

const PRESENCE_JOIN_SCHEMA = z.object({
    type: z.literal('presence_join'),
    buildhub_id: z.number(),
    page_url: z.string(),
    user: PRESENCE_USER_SCHEMA,
});

const PRESENCE_LEAVE_SCHEMA = z.object({
    type: z.literal('presence_leave'),
    buildhub_id: z.number(),
    page_url: z.string(),
    user: PRESENCE_USER_SCHEMA,
});

const PRESENCE_UPDATE_SCHEMA = z.object({
    type: z.literal('presence_update'),
    buildhub_id: z.number(),
    page_url: z.string(),
    user: PRESENCE_USER_SCHEMA,
});

const BUILDHUB_MESSAGE_SCHEMA = z.discriminatedUnion('type', [
    VOTE_UPDATE_SCHEMA,
    COMMENT_UPDATE_SCHEMA,
    PRESENCE_JOIN_SCHEMA,
    PRESENCE_LEAVE_SCHEMA,
    PRESENCE_UPDATE_SCHEMA,
]);

type VoteUpdateMessage = z.infer<typeof VOTE_UPDATE_SCHEMA>;
type CommentUpdateMessage = z.infer<typeof COMMENT_UPDATE_SCHEMA>;
type PresenceJoinMessage = z.infer<typeof PRESENCE_JOIN_SCHEMA>;
type PresenceLeaveMessage = z.infer<typeof PRESENCE_LEAVE_SCHEMA>;
type PresenceUpdateMessage = z.infer<typeof PRESENCE_UPDATE_SCHEMA>;
type PresenceMessage =
    | PresenceJoinMessage
    | PresenceLeaveMessage
    | PresenceUpdateMessage;
export type PresenceUser = z.infer<typeof PRESENCE_USER_SCHEMA>;
type BuildHubMessage = z.infer<typeof BUILDHUB_MESSAGE_SCHEMA>;

interface BuildHubRealTimeContextValue {
    subscribeToVoteUpdates: (
        callback: (message: VoteUpdateMessage) => void
    ) => () => void;
    subscribeToCommentUpdates: (
        callback: (message: CommentUpdateMessage) => void
    ) => () => void;
    subscribeToPresenceUpdates: (
        callback: (message: PresenceMessage) => void
    ) => () => void;
    sendPresenceMessage: (
        message: Omit<PresenceMessage, 'buildhub_id'>
    ) => void;
    /**
     * Manually trigger comment update callbacks (useful for immediate updates after mutation)
     * This simulates a WebSocket message without waiting for the actual WebSocket
     */
    triggerCommentUpdate: (message: CommentUpdateMessage) => void;
    isConnected: boolean;
}

const BuildHubRealTimeContext =
    createContext<BuildHubRealTimeContextValue | null>(null);

interface BuildHubRealTimeProviderProps {
    buildHubId: number;
    children: React.ReactNode;
}

/**
 * Provider that manages WebSocket connection and subscription for BuildHub real-time updates.
 * Should be placed at the root level (e.g., in _app._pm.app.project.$id.tsx) to ensure
 * only one WebSocket connection per BuildHub.
 */
export function BuildHubRealTimeProvider({
    buildHubId,
    children,
}: BuildHubRealTimeProviderProps) {
    const [token] = useCookieState('__ap_tkn');
    const webSocketUrl = useAppData().webSocketUrl;
    const subscribedRef = useRef(false);
    // Store callbacks directly in refs for immediate execution (like chat)
    const voteCallbacksRef = useRef<Set<(message: VoteUpdateMessage) => void>>(
        new Set()
    );
    const commentCallbacksRef = useRef<
        Set<(message: CommentUpdateMessage) => void>
    >(new Set());
    const presenceCallbacksRef = useRef<
        Set<(message: PresenceMessage) => void>
    >(new Set());
    const readyStateRef = useRef<ReadyState>(ReadyState.CONNECTING);
    const sendMessageRef = useRef<((message: string) => void) | undefined>(
        undefined
    );

    const { sendMessage, lastJsonMessage, readyState } =
        useWebSocket<BuildHubMessage>(webSocketUrl, {
            queryParams: token ? { token } : undefined,
            share: true,
            heartbeat: {
                timeout: 10_000,
                interval: 60_000,
            },
            filter: (message) => {
                if (isHeartbeatMessage(message.data)) {
                    return false;
                }
                if (typeof message.data !== 'string') {
                    return false;
                }
                try {
                    const parsed = JSON.parse(message.data);
                    BUILDHUB_MESSAGE_SCHEMA.parse(parsed);
                    // Note: buildhub_id filtering moved to callback level for better performance
                    return true;
                } catch (error) {
                    return false;
                }
            },
            shouldReconnect: () => !!token,
            reconnectAttempts: 20,
            reconnectInterval: (lastAttemptNumber) => {
                if (lastAttemptNumber <= 2) {
                    return 2_000; // 2 seconds
                }
                const baseDelay = 30_000; // 30 seconds
                const maxDelay = 3_600_000; // 1 hour
                const exponent = lastAttemptNumber - 3;
                return Math.min(baseDelay * Math.pow(2, exponent), maxDelay);
            },
        });

    // Keep refs in sync with current values
    useEffect(() => {
        readyStateRef.current = readyState;
        sendMessageRef.current = sendMessage;
    }, [readyState, sendMessage]);

    // Subscribe to BuildHub channel when connected
    useEffect(() => {
        if (readyState === ReadyState.OPEN && !subscribedRef.current) {
            const channelName = `buildhub:${buildHubId}`;
            sendMessage(
                JSON.stringify({
                    type: 'subscribe',
                    channel: channelName,
                })
            );
            subscribedRef.current = true;
        } else if (readyState !== ReadyState.OPEN) {
            subscribedRef.current = false;
        }
    }, [readyState, buildHubId, sendMessage]);

    // Unsubscribe when component unmounts or buildHubId changes
    useEffect(() => {
        return () => {
            if (
                readyStateRef.current === ReadyState.OPEN &&
                subscribedRef.current &&
                sendMessageRef.current
            ) {
                const channelName = `buildhub:${buildHubId}`;
                sendMessageRef.current(
                    JSON.stringify({
                        type: 'unsubscribe',
                        channel: channelName,
                    })
                );
            }
        };
    }, [buildHubId]);

    // Handle incoming messages and call callbacks directly (like chat)
    useEffect(() => {
        if (!lastJsonMessage) {
            return;
        }

        // Filter by buildhub_id at callback level (only runs for registered callbacks)
        if (lastJsonMessage.buildhub_id !== buildHubId) {
            return;
        }

        // Direct callback execution - no intermediate dispatch layer
        if (lastJsonMessage.type === 'vote') {
            voteCallbacksRef.current.forEach((callback) => {
                callback(lastJsonMessage);
            });
        } else if (lastJsonMessage.type === 'comment') {
            commentCallbacksRef.current.forEach((callback) => {
                callback(lastJsonMessage);
            });
        } else if (
            lastJsonMessage.type === 'presence_join' ||
            lastJsonMessage.type === 'presence_leave' ||
            lastJsonMessage.type === 'presence_update'
        ) {
            presenceCallbacksRef.current.forEach((callback) => {
                callback(lastJsonMessage);
            });
        }
    }, [lastJsonMessage, buildHubId]);

    // Subscribe to vote updates - direct callback registration
    const subscribeToVoteUpdates = useCallback(
        (callback: (message: VoteUpdateMessage) => void) => {
            voteCallbacksRef.current.add(callback);
            return () => {
                voteCallbacksRef.current.delete(callback);
            };
        },
        []
    );

    // Subscribe to comment updates - direct callback registration
    const subscribeToCommentUpdates = useCallback(
        (callback: (message: CommentUpdateMessage) => void) => {
            commentCallbacksRef.current.add(callback);
            return () => {
                commentCallbacksRef.current.delete(callback);
            };
        },
        []
    );

    // Subscribe to presence updates - direct callback registration
    const subscribeToPresenceUpdates = useCallback(
        (callback: (message: PresenceMessage) => void) => {
            presenceCallbacksRef.current.add(callback);
            return () => {
                presenceCallbacksRef.current.delete(callback);
            };
        },
        []
    );

    // Send presence message to WebSocket server
    const sendPresenceMessage = useCallback(
        (message: Omit<PresenceMessage, 'buildhub_id'>) => {
            if (readyState === ReadyState.OPEN) {
                const fullMessage = {
                    ...message,
                    buildhub_id: buildHubId,
                };
                sendMessage(JSON.stringify(fullMessage));
            }
        },
        [readyState, buildHubId, sendMessage]
    );

    // Manually trigger comment update callbacks (for immediate updates after mutation)
    const triggerCommentUpdate = useCallback(
        (message: CommentUpdateMessage) => {
            // Filter by buildhub_id (same as WebSocket handler)
            if (message.buildhub_id !== buildHubId) {
                return;
            }
            // Call all registered callbacks immediately
            commentCallbacksRef.current.forEach((callback) => {
                callback(message);
            });
        },
        [buildHubId]
    );

    const value: BuildHubRealTimeContextValue = {
        subscribeToVoteUpdates,
        subscribeToCommentUpdates,
        subscribeToPresenceUpdates,
        sendPresenceMessage,
        triggerCommentUpdate,
        isConnected: readyState === ReadyState.OPEN,
    };

    return (
        <BuildHubRealTimeContext.Provider value={value}>
            {children}
        </BuildHubRealTimeContext.Provider>
    );
}

/**
 * Hook to access BuildHub real-time context.
 * Must be used within BuildHubRealTimeProvider.
 */
export function useBuildHubRealTimeContext(): BuildHubRealTimeContextValue {
    const context = useContext(BuildHubRealTimeContext);
    if (!context) {
        throw new Error(
            'useBuildHubRealTimeContext must be used within BuildHubRealTimeProvider'
        );
    }
    return context;
}

/**
 * Hook to subscribe to vote updates for a specific document.
 * Automatically filters messages for the given documentId.
 */
export function useBuildHubVoteUpdates(
    documentId: number,
    onUpdate: (message: VoteUpdateMessage) => void
) {
    const { subscribeToVoteUpdates } = useBuildHubRealTimeContext();
    const latestOnUpdate = useLatest(onUpdate);

    useEffect(() => {
        return subscribeToVoteUpdates((message) => {
            if (message.document_id === documentId) {
                latestOnUpdate.current?.(message);
            }
        });
    }, [documentId, subscribeToVoteUpdates, latestOnUpdate]);
}

/**
 * Hook to subscribe to comment updates for a specific document.
 * Automatically filters messages for the given documentId.
 */
export function useBuildHubCommentUpdates(
    documentId: number,
    onUpdate: (message: CommentUpdateMessage) => void
) {
    const { subscribeToCommentUpdates } = useBuildHubRealTimeContext();
    const latestOnUpdate = useLatest(onUpdate);

    useEffect(() => {
        return subscribeToCommentUpdates((message) => {
            if (message.document_id === documentId) {
                latestOnUpdate.current?.(message);
            }
        });
    }, [documentId, subscribeToCommentUpdates, latestOnUpdate]);
}

/**
 * Hook to subscribe to all vote updates for the BuildHub.
 */
export function useBuildHubAllVoteUpdates(
    onUpdate: (message: VoteUpdateMessage) => void
) {
    const { subscribeToVoteUpdates } = useBuildHubRealTimeContext();
    const latestOnUpdate = useLatest(onUpdate);

    useEffect(() => {
        return subscribeToVoteUpdates((message) => {
            latestOnUpdate.current?.(message);
        });
    }, [subscribeToVoteUpdates, latestOnUpdate]);
}

/**
 * Hook to subscribe to all comment updates for the BuildHub.
 */
export function useBuildHubAllCommentUpdates(
    onUpdate: (message: CommentUpdateMessage) => void
) {
    const { subscribeToCommentUpdates } = useBuildHubRealTimeContext();
    const latestOnUpdate = useLatest(onUpdate);

    useEffect(() => {
        return subscribeToCommentUpdates((message) => {
            latestOnUpdate.current?.(message);
        });
    }, [subscribeToCommentUpdates, latestOnUpdate]);
}

/**
 * Check if a message from the WebSocket is a heartbeat message.
 */
function isHeartbeatMessage(message: unknown) {
    return !message || message === 'ping' || message === 'pong';
}
